/**
 * The View As stash: the master's own session, parked in this tab's
 * sessionStorage while they view the system as someone else.
 *
 * It used to be trusted on sight. Sign Out left it behind, so the next person to
 * sign in on that tab saw "Viewing as …" and one click on Exit handed them the
 * master's session (audit A, B2). Now the stash is believed only while the
 * token in use is a View As token (it carries `imp`, the master's id) AND the
 * stash belongs to that same master. Anything else is cleared.
 *
 * A second tab opened during View As shares the token (localStorage) but not
 * the stash (sessionStorage). It is still told it is viewing as someone, and its
 * only way out is to sign out.
 */
import { STORAGE_KEYS } from '../config/app';

export const MASTER_TOKEN_KEY = 'masterToken';
export const MASTER_SNAPSHOT_KEY = 'masterAdminSnapshot';

/** The payload of a JWT, or null. Read for display decisions only, never trusted by the server. */
export function tokenClaims(token) {
  try {
    const part = String(token).split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')));
  } catch {
    return null;
  }
}

/** Forget the parked master session. Called on sign-out, sign-in and whenever it is not believed. */
export function clearImpersonationStash() {
  try {
    sessionStorage.removeItem(MASTER_TOKEN_KEY);
    sessionStorage.removeItem(MASTER_SNAPSHOT_KEY);
  } catch { /* storage unavailable: nothing to clear */ }
}

/**
 * Where this tab stands.
 * @returns {{ impersonating: boolean, snapshot: object|null, masterToken: string|null }}
 *   `snapshot` and `masterToken` are set only when the way back is trusted.
 */
export function readImpersonation() {
  let token = null;
  let snapshot = null;
  let masterToken = null;
  try {
    token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    masterToken = sessionStorage.getItem(MASTER_TOKEN_KEY);
    const raw = sessionStorage.getItem(MASTER_SNAPSHOT_KEY);
    snapshot = raw ? JSON.parse(raw) : null;
  } catch {
    snapshot = null;
  }

  const imp = tokenClaims(token)?.imp;
  if (imp === undefined || imp === null) {
    if (masterToken || snapshot) clearImpersonationStash();
    return { impersonating: false, snapshot: null, masterToken: null };
  }

  const trusted = Boolean(snapshot && masterToken)
    && String(snapshot.id) === String(imp)
    && String(tokenClaims(masterToken)?.userId) === String(imp);
  if (!trusted) {
    if (masterToken || snapshot) clearImpersonationStash();
    return { impersonating: true, snapshot: null, masterToken: null };
  }
  return { impersonating: true, snapshot, masterToken };
}
